import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { UUID } from 'crypto'

import {
  listProfileRevisions,
  getProfileRevision,
  updateProfileText,
} from '@/api/orgsAPI'
import { ProfileRevisionSummary, ProfileRevisionDetail } from '@/models/Orgs'
import { localisedDate } from '@/utils/dateUtils'
import { unifiedToRows, revisionWhatChangedKind } from '@/utils/profileDiff'
import { useProfileRegenGuard } from './ProfileRegenBlockProvider'

interface Props {
  open: boolean
  onClose: () => void
  token: string
  orgUuid: UUID
  onRestored: () => void
}

// Added text is underlined and removed text struck through, so a change is legible
// without relying on colour (WCAG 1.4.1); rendered as <ins>/<del> so screen readers
// announce it too.
const addSx = {
  color: 'success.dark',
  backgroundColor: 'rgba(46,125,50,0.05)',
  textDecoration: 'underline',
}
const remSx = {
  color: '#b71c1c',
  backgroundColor: 'rgba(211,47,47,0.05)',
  textDecoration: 'line-through',
}

function describeAuthor(by: string | null): string {
  if (!by || by === 'auto') return 'Auto-update'
  if (by === 'regenerate') return 'Regenerated from scratch'
  if (by === 'system') return 'Original profile'
  // Anything else is a manual edit — `by` holds the editor's name.
  return `Edited by ${by}`
}

// The "what changed" area: auto-regens carry a plain-language summary; manual edits
// have none, so fall back to the diff. Returns null when there's nothing to show.
function WhatChanged({ detail }: { detail: ProfileRevisionDetail }) {
  const kind = revisionWhatChangedKind(detail)

  if (kind === 'none') return null

  if (kind === 'summary') {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          What changed in this version
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          {detail.changes_summary?.map((c, i) => (
            <li key={i}>
              <Typography variant="body2">{c}</Typography>
            </li>
          ))}
        </Box>
      </Box>
    )
  }

  if (kind === 'nochange') {
    // An auto-update that produced an identical profile.
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          What changed in this version
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No changes — this update produced the same profile as the previous
          version.
        </Typography>
      </Box>
    )
  }

  const rows = unifiedToRows(detail.diff?.unified)
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        What changed in this version
      </Typography>
      <Alert
        severity="info"
        variant="outlined"
        icon={false}
        sx={{ mb: 1.5, border: 1, borderColor: 'primary.light' }}
      >
        <Typography variant="caption" color="text.secondary">
          Highlights show what changed from the previous version:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 3,
            mt: 0.75,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="ins" aria-hidden sx={{ ...addSx, px: 0.5 }}>
              text
            </Box>
            <Typography variant="caption" color="text.secondary">
              Added in this version
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="del" aria-hidden sx={{ ...remSx, px: 0.5 }}>
              text
            </Box>
            <Typography variant="caption" color="text.secondary">
              Removed in this version
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          {detail.diff?.from_version
            ? `Compared with the previous version (v${detail.diff.from_version})`
            : 'Compared with the previous version'}
        </Typography>
      </Alert>
      <Box
        sx={{ wordBreak: 'break-word', fontSize: '0.875rem', lineHeight: 1.6 }}
      >
        {rows.map((row, i) => {
          if (row.kind === 'added') {
            return (
              <Box key={i} component="ins" sx={{ ...addSx, display: 'block' }}>
                {row.text}
              </Box>
            )
          }
          if (row.kind === 'removed') {
            return (
              <Box key={i} component="del" sx={{ ...remSx, display: 'block' }}>
                {row.text}
              </Box>
            )
          }
          return (
            <Box key={i} component="div">
              {row.tokens.map((tk, k) => {
                if (tk.t === 'same') return <span key={k}>{tk.s}</span>
                return (
                  <Box
                    key={k}
                    component={tk.t === 'add' ? 'ins' : 'del'}
                    sx={tk.t === 'add' ? addSx : remSx}
                  >
                    {tk.s}
                  </Box>
                )
              })}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default function ProfileRevisionHistoryDialog({
  open,
  onClose,
  token,
  orgUuid,
  onRestored,
}: Props) {
  const [revisions, setRevisions] = useState<ProfileRevisionSummary[] | null>(
    null,
  )
  // Each expanded version is fetched once and cached here.
  const [details, setDetails] = useState<Record<number, ProfileRevisionDetail>>(
    {},
  )
  const [expanded, setExpanded] = useState<number | false>(false)
  const [listLoading, setListLoading] = useState(false)
  const [loadingVersion, setLoadingVersion] = useState<number | null>(null)
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const guardRegen = useProfileRegenGuard()

  useEffect(() => {
    if (!open) {
      setExpanded(false)
      setDetails({})
      return
    }
    setListLoading(true)
    setError(null)
    listProfileRevisions(token, orgUuid)
      .then(setRevisions)
      .catch(() => setError('Could not load your profile history.'))
      .finally(() => setListLoading(false))
  }, [open, token, orgUuid])

  function handleAccordionChange(version: number) {
    return async (_e: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? version : false)
      if (!isExpanded || details[version]) return
      setLoadingVersion(version)
      setError(null)
      try {
        const detail = await getProfileRevision(token, orgUuid, version)
        setDetails((prev) => ({ ...prev, [version]: detail }))
      } catch {
        setError('Could not load this version.')
        setExpanded(false)
      } finally {
        setLoadingVersion(null)
      }
    }
  }

  async function handleRestore(detail: ProfileRevisionDetail) {
    if (!detail.profile_text) return
    setRestoringVersion(detail.version)
    setError(null)
    try {
      // Restore is just a manual save of the old text — force past the quality
      // gate (it was a valid profile) and it lands as a new revision.
      const result = await updateProfileText(
        token,
        orgUuid,
        detail.profile_text,
        true,
      )
      // A non-200 returns { error } rather than throwing — don't report success.
      if (result?.error) {
        setError('Could not restore this version. Please try again.')
        return
      }
      onRestored()
      onClose()
    } catch (err) {
      // A regen-in-progress 409 goes through the shared block dialog, not a
      // generic error message.
      if (guardRegen(err)) return
      setError('Could not restore this version. Please try again.')
    } finally {
      setRestoringVersion(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Profile revision history</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {listLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!listLoading && revisions && revisions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No previous versions yet.
          </Typography>
        )}

        {!listLoading &&
          (revisions || []).map((rev) => {
            const isSaved = rev.status === 'saved'
            const headerId = `profile-rev-${rev.version}-header`
            const contentId = `profile-rev-${rev.version}-content`
            const detail = details[rev.version]
            return (
              <Accordion
                key={rev.version}
                expanded={isSaved && expanded === rev.version}
                onChange={
                  isSaved ? handleAccordionChange(rev.version) : undefined
                }
                disabled={!isSaved}
                disableGutters
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #eee',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    isSaved ? <ExpandMoreIcon aria-hidden /> : undefined
                  }
                  aria-controls={contentId}
                  id={headerId}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {describeAuthor(rev.data_entry_by)}
                      {rev.status === 'failed' && (
                        <Chip
                          label="update failed"
                          size="small"
                          color="warning"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      v{rev.version} · {localisedDate(new Date(rev.date))}
                    </Typography>
                    {rev.status === 'failed' && rev.error_message && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {rev.error_message}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                {isSaved && (
                  <AccordionDetails
                    role="region"
                    aria-labelledby={headerId}
                    id={contentId}
                  >
                    {loadingVersion === rev.version && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          py: 3,
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {detail && (
                      <>
                        <Divider sx={{ mb: 2 }} />
                        <WhatChanged detail={detail} />
                        <Divider sx={{ my: 2 }} />
                        <Box
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: '#f5f5f5',
                            maxHeight: '320px',
                            overflowY: 'auto',
                          }}
                        >
                          <Typography variant="body2">
                            {detail.profile_text}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            disableRipple
                            onClick={() => handleRestore(detail)}
                            disabled={
                              restoringVersion !== null || !detail.profile_text
                            }
                            sx={{ bgcolor: '#1C3045', textTransform: 'none' }}
                          >
                            {restoringVersion === rev.version
                              ? 'Restoring…'
                              : 'Restore this version'}
                          </Button>
                        </Box>
                      </>
                    )}
                  </AccordionDetails>
                )}
              </Accordion>
            )
          })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disableRipple>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
